/**
 * Reference Tracking Service
 * Finds all references to entities across the system
 */

import { getCachedTemplates } from '../stores/templatesStore';
import { objectsStore } from '../stores/objectsStore';
import { getCachedTypes } from '../stores/typesStore';
import { getCachedStringsItems } from '../stores/stringsStore';
import { getCachedDialogs } from '../stores/dialogs/cache/byGame';
import type { ObjectItem, FormFieldObject } from '../stores/objectsStore';
import type { Dialog } from '../stores/dialogs/types';
import type { GlossaryBlueprint } from '@/types-games';

export type EntityType = 'type' | 'string' | 'object' | 'template' | 'tag' | 'folder';

export interface ReferenceLocation {
  entityType: 'template' | 'object' | 'filter' | 'exportTemplate';
  entityId: string;
  entityName: string;
  location: string; // Human-readable: "Field 'Character Type' in section 'Stats'"
  path: string[]; // Machine-readable path to the reference
}

export interface ReferenceResult {
  isReferenced: boolean;
  references: ReferenceLocation[];
  totalCount: number;
}

export interface BrokenReference {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  referenceType: EntityType;
  missingId: string;
  location: string;
  path: string[];
}

export interface SanityCheckResult {
  hasBrokenReferences: boolean;
  brokenReferences: BrokenReference[];
  totalCount: number;
  byType: {
    types: number;
    strings: number;
    objects: number;
    templates: number;
    tags: number;
    folders: number;
  };
}

/**
 * Find all references to a specific entity
 */
export function findReferences(
  entityId: string,
  entityType: EntityType,
  gameId: string
): ReferenceResult {
  const references: ReferenceLocation[] = [];

  switch (entityType) {
    case 'type':
      references.push(...findTypeReferences(entityId, gameId));
      break;
    case 'string':
      references.push(...findStringReferences(entityId, gameId));
      break;
    case 'object':
      references.push(...findObjectReferences(entityId, gameId));
      break;
    case 'template':
      references.push(...findTemplateReferences(entityId, gameId));
      break;
    case 'tag':
      references.push(...findTagReferences(entityId, gameId));
      break;
    case 'folder':
      references.push(...findFolderReferences(entityId, gameId));
      break;
  }

  return {
    isReferenced: references.length > 0,
    references,
    totalCount: references.length,
  };
}

/**
 * Find references to a type
 */
function findTypeReferences(typeId: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  // Check blueprints for filterOptions
  const templates = getCachedTemplates(gameId);
  for (const template of templates) {
    const templateRefs = findTypeInTemplate(typeId, template);
    references.push(...templateRefs);
  }
  
  // Check objects for type field values
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  for (const obj of objects) {
    const objectRefs = findTypeInObject(typeId, obj);
    references.push(...objectRefs);
  }
  
  return references;
}

/**
 * Find type references in a template (filterOptions)
 */
function findTypeInTemplate(typeId: string, template: GlossaryBlueprint): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  if (!template.data || typeof template.data !== 'object') {
    return references;
  }
  
  const data = template.data as any;
  
  // Handle both array format (old) and object format (new)
  if (Array.isArray(data.sections)) {
    // Array format: sections is an array of section objects with fields array
    for (const section of data.sections) {
      if (!section.fields) continue;
      
      for (const field of section.fields) {
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          if (field.filterOptions.includes(typeId)) {
            references.push({
              entityType: 'template',
              entityId: template.id,
              entityName: template.name,
              location: `Field '${field.label || field.id}' in section '${section.title}' (filterOptions)`,
              path: ['data', 'sections', section.id, 'fields', field.id, 'filterOptions'],
            });
          }
        }
      }
    }
  } else if (data.sections && typeof data.sections === 'object') {
    // Object format: sections is an object with section IDs as keys
    for (const [sectionId, sectionData] of Object.entries(data.sections)) {
      const section = sectionData as any;
      const sectionTitle = section._sectionTitle || 'Untitled Section';
      
      // Fields are stored as keys in the section object (excluding metadata keys starting with _)
      for (const [fieldId, fieldData] of Object.entries(section)) {
        if (fieldId.startsWith('_') || fieldId === 'hiddenForRoles' || fieldId === 'readOnlyForRoles') continue;
        
        const field = fieldData as any;
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          if (field.filterOptions.includes(typeId)) {
            references.push({
              entityType: 'template',
              entityId: template.id,
              entityName: template.name,
              location: `Field '${field.label || field.id}' in section '${sectionTitle}' (filterOptions)`,
              path: ['data', 'sections', sectionId, fieldId, 'filterOptions'],
            });
          }
        }
      }
    }
  }
  
  return references;
}

/**
 * Find type references in an object (field values)
 */
function findTypeInObject(typeId: string, obj: ObjectItem): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  for (const section of obj.data) {
    for (const field of section.fields) {
      if (field.type === 'types') {
        const value = field.value;
        let hasReference = false;
        
        // Check single value
        if (typeof value === 'string' && value === typeId) {
          hasReference = true;
        }
        
        // Check array of values
        if (Array.isArray(value) && value.includes(typeId)) {
          hasReference = true;
        }
        
        if (hasReference) {
          references.push({
            entityType: 'object',
            entityId: obj.id,
            entityName: obj.name,
            location: `Field '${field.label}' in section '${section.title}'`,
            path: ['data', section.id, 'fields', field.id, 'value'],
          });
        }
      }
    }
  }
  
  return references;
}

/**
 * Find references to a string (TMS entry)
 */
function findStringReferences(stringId: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  
  for (const obj of objects) {
    for (const section of obj.data) {
      for (const field of section.fields) {
        // Check single translatable field (legacy format)
        if (field.string_id === stringId) {
          references.push({
            entityType: 'object',
            entityId: obj.id,
            entityName: obj.name,
            location: `Field '${field.label}' in section '${section.title}' (string_id)`,
            path: ['data', section.id, 'fields', field.id, 'string_id'],
          });
        }
        
        // Check multiple translatable fields (TranslatableValue[] format)
        if (field.translation && field.multiple && Array.isArray(field.value)) {
          field.value.forEach((item: any, index: number) => {
            if (item && typeof item === 'object' && item.string_id === stringId) {
              references.push({
                entityType: 'object',
                entityId: obj.id,
                entityName: obj.name,
                location: `Field '${field.label}' in section '${section.title}' [item ${index + 1}]`,
                path: ['data', section.id, 'fields', field.id, 'value', index.toString(), 'string_id'],
              });
            }
          });
        }
      }
    }
  }
  
  return references;
}

/**
 * Find references to an object (in other objects' fields)
 */
function findObjectReferences(objectId: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  
  for (const obj of objects) {
    // Don't check the object against itself
    if (obj.id === objectId) continue;
    
    for (const section of obj.data) {
      for (const field of section.fields) {
        if (field.type === 'glossaryItem') {
          const value = field.value;
          let hasReference = false;
          
          // Check single value
          if (typeof value === 'string' && value === objectId) {
            hasReference = true;
          }
          
          // Check array of values
          if (Array.isArray(value) && value.includes(objectId)) {
            hasReference = true;
          }
          
          if (hasReference) {
            references.push({
              entityType: 'object',
              entityId: obj.id,
              entityName: obj.name,
              location: `Field '${field.label}' in section '${section.title}' (references this object)`,
              path: ['data', section.id, 'fields', field.id, 'value'],
            });
          }
        }
      }
    }
  }
  
  return references;
}

/**
 * Find references to a template (objects using blueprint_id)
 */
function findTemplateReferences(templateId: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  
  for (const obj of objects) {
    if (obj.blueprint_id === templateId) {
      references.push({
        entityType: 'object',
        entityId: obj.id,
        entityName: obj.name,
        location: `Object uses this template as its blueprint`,
        path: ['blueprint_id'],
      });
    }
  }
  
  return references;
}

/**
 * Find references to a tag
 */
function findTagReferences(tag: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  // Check objects
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  for (const obj of objects) {
    if (obj.tags && obj.tags.includes(tag)) {
      references.push({
        entityType: 'object',
        entityId: obj.id,
        entityName: obj.name,
        location: `Object has this tag`,
        path: ['tags'],
      });
    }
  }
  
  // Check templates
  const templates = getCachedTemplates(gameId);
  for (const template of templates) {
    if (template.tags && template.tags.includes(tag)) {
      references.push({
        entityType: 'template',
        entityId: template.id,
        entityName: template.name,
        location: `Template has this tag`,
        path: ['tags'],
      });
    }
  }
  
  return references;
}

/**
 * Find references to a folder
 */
function findFolderReferences(folderId: string, gameId: string): ReferenceLocation[] {
  const references: ReferenceLocation[] = [];
  
  // Check objects
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  for (const obj of objects) {
    if (obj.folder === folderId) {
      references.push({
        entityType: 'object',
        entityId: obj.id,
        entityName: obj.name,
        location: `Object is in this folder`,
        path: ['folder'],
      });
    }
  }
  
  // Check templates
  const templates = getCachedTemplates(gameId);
  for (const template of templates) {
    if (template.folder === folderId) {
      references.push({
        entityType: 'template',
        entityId: template.id,
        entityName: template.name,
        location: `Template is in this folder`,
        path: ['folder'],
      });
    }
  }
  
  return references;
}

/**
 * Scan entire game for broken references
 */
export function findBrokenReferences(gameId: string): SanityCheckResult {
  const brokenReferences: BrokenReference[] = [];
  
  // Get all valid entity IDs
  const validTypes = new Set(getCachedTypes(gameId).map(t => t.id));
  const validStrings = new Set(getCachedStringsItems(gameId).map(s => s.id));
  const validObjects = new Set(objectsStore.getData().filter(o => o.game_id === gameId).map(o => o.id));
  const validTemplates = new Set(getCachedTemplates(gameId).map(t => t.id));
  
  // Check templates for broken references
  const templates = getCachedTemplates(gameId);
  for (const template of templates) {
    brokenReferences.push(...findBrokenReferencesInTemplate(template, validTypes));
  }
  
  // Check objects for broken references
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  for (const obj of objects) {
    brokenReferences.push(...findBrokenReferencesInObject(obj, validTypes, validStrings, validObjects, validTemplates));
  }

  // Check dialogs for broken string references
  const dialogs = getCachedDialogs(gameId);
  for (const dialog of dialogs) {
    brokenReferences.push(...findBrokenReferencesInDialog(dialog, validStrings));
  }
  
  // Count by type
  const byType = {
    types: brokenReferences.filter(r => r.referenceType === 'type').length,
    strings: brokenReferences.filter(r => r.referenceType === 'string').length,
    objects: brokenReferences.filter(r => r.referenceType === 'object').length,
    templates: brokenReferences.filter(r => r.referenceType === 'template').length,
    tags: brokenReferences.filter(r => r.referenceType === 'tag').length,
    folders: brokenReferences.filter(r => r.referenceType === 'folder').length,
  };
  
  return {
    hasBrokenReferences: brokenReferences.length > 0,
    brokenReferences,
    totalCount: brokenReferences.length,
    byType,
  };
}

/**
 * Find broken references in a template
 */
function findBrokenReferencesInTemplate(
  template: GlossaryBlueprint,
  validTypes: Set<string>
): BrokenReference[] {
  const broken: BrokenReference[] = [];
  
  if (!template.data || typeof template.data !== 'object') {
    return broken;
  }
  
  const data = template.data as any;
  
  // Handle both array format (old) and object format (new)
  if (Array.isArray(data.sections)) {
    // Array format
    for (const section of data.sections) {
      if (!section.fields) continue;
      
      for (const field of section.fields) {
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          for (const typeId of field.filterOptions) {
            if (!validTypes.has(typeId)) {
              broken.push({
                entityType: 'template',
                entityId: template.id,
                entityName: template.name,
                referenceType: 'type',
                missingId: typeId,
                location: `Field '${field.label || field.id}' in section '${section.title}' references missing type`,
                path: ['data', 'sections', section.id, 'fields', field.id, 'filterOptions'],
              });
            }
          }
        }
      }
    }
  } else if (data.sections && typeof data.sections === 'object') {
    // Object format: sections is an object with section IDs as keys
    for (const [sectionId, sectionData] of Object.entries(data.sections)) {
      const section = sectionData as any;
      const sectionTitle = section._sectionTitle || 'Untitled Section';
      
      // Fields are stored as keys in the section object (excluding metadata keys)
      for (const [fieldId, fieldData] of Object.entries(section)) {
        if (fieldId.startsWith('_') || fieldId === 'hiddenForRoles' || fieldId === 'readOnlyForRoles') continue;
        
        const field = fieldData as any;
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          for (const typeId of field.filterOptions) {
            if (!validTypes.has(typeId)) {
              broken.push({
                entityType: 'template',
                entityId: template.id,
                entityName: template.name,
                referenceType: 'type',
                missingId: typeId,
                location: `Field '${field.label || field.id}' in section '${sectionTitle}' references missing type`,
                path: ['data', 'sections', sectionId, fieldId, 'filterOptions'],
              });
            }
          }
        }
      }
    }
  }
  
  return broken;
}

/**
 * Find broken references in an object
 */
function findBrokenReferencesInObject(
  obj: ObjectItem,
  validTypes: Set<string>,
  validStrings: Set<string>,
  validObjects: Set<string>,
  validTemplates: Set<string>
): BrokenReference[] {
  const broken: BrokenReference[] = [];
  
  // Check blueprint_id
  if (obj.blueprint_id && !validTemplates.has(obj.blueprint_id)) {
    broken.push({
      entityType: 'object',
      entityId: obj.id,
      entityName: obj.name,
      referenceType: 'template',
      missingId: obj.blueprint_id,
      location: `Object references missing template`,
      path: ['blueprint_id'],
    });
  }
  
  // Check fields
  for (const section of obj.data) {
    for (const field of section.fields) {
      // Check type references
      if (field.type === 'types') {
        const value = field.value;
        const typeIds: string[] = [];
        
        if (typeof value === 'string') {
          typeIds.push(value);
        } else if (Array.isArray(value)) {
          typeIds.push(...value.filter(v => typeof v === 'string'));
        }
        
        for (const typeId of typeIds) {
          if (typeId && !validTypes.has(typeId)) {
            broken.push({
              entityType: 'object',
              entityId: obj.id,
              entityName: obj.name,
              referenceType: 'type',
              missingId: typeId,
              location: `Field '${field.label}' in section '${section.title}' references missing type`,
              path: ['data', section.id, 'fields', field.id, 'value'],
            });
          }
        }
      }
      
      // Check string references
      if (field.string_id && !validStrings.has(field.string_id)) {
        broken.push({
          entityType: 'object',
          entityId: obj.id,
          entityName: obj.name,
          referenceType: 'string',
          missingId: field.string_id,
          location: `Field '${field.label}' in section '${section.title}' references missing string`,
          path: ['data', section.id, 'fields', field.id, 'string_id'],
        });
      }
      
      // Check multiple translatable fields
      if (field.translation && field.multiple && Array.isArray(field.value)) {
        field.value.forEach((item: any, index: number) => {
          if (item && typeof item === 'object' && item.string_id && !validStrings.has(item.string_id)) {
            broken.push({
              entityType: 'object',
              entityId: obj.id,
              entityName: obj.name,
              referenceType: 'string',
              missingId: item.string_id,
              location: `Field '${field.label}' in section '${section.title}' [item ${index + 1}] references missing string`,
              path: ['data', section.id, 'fields', field.id, 'value', index.toString(), 'string_id'],
            });
          }
        });
      }
      
      // Check object references
      if (field.type === 'glossaryItem') {
        const value = field.value;
        const objectIds: string[] = [];
        
        if (typeof value === 'string') {
          objectIds.push(value);
        } else if (Array.isArray(value)) {
          objectIds.push(...value.filter(v => typeof v === 'string'));
        }
        
        for (const objectId of objectIds) {
          if (objectId && !validObjects.has(objectId)) {
            broken.push({
              entityType: 'object',
              entityId: obj.id,
              entityName: obj.name,
              referenceType: 'object',
              missingId: objectId,
              location: `Field '${field.label}' in section '${section.title}' references missing object`,
              path: ['data', section.id, 'fields', field.id, 'value'],
            });
          }
        }
      }
    }
  }
  
  return broken;
}

/**
 * Find broken string references in a dialog (nodes, options, and legacy fields)
 */
function findBrokenReferencesInDialog(
  dialog: Dialog,
  validStrings: Set<string>
): BrokenReference[] {
  const broken: BrokenReference[] = [];
  const data = dialog.data;
  if (!data) return broken;

  // New structure: nodes
  if (data.nodes && Array.isArray(data.nodes)) {
    for (const node of data.nodes) {
      if (node.string_id && !validStrings.has(node.string_id)) {
        broken.push({
          entityType: 'object',
          entityId: dialog.id,
          entityName: dialog.name,
          referenceType: 'string',
          missingId: node.string_id,
          location: `Dialog node '${node.name || node.node_identifier}' references missing string`,
          path: ['data', 'nodes', node.id, 'string_id'],
        });
      }

      if (node.options) {
        for (const option of node.options) {
          if (option.string_id && !validStrings.has(option.string_id)) {
            broken.push({
              entityType: 'object',
              entityId: dialog.id,
              entityName: dialog.name,
              referenceType: 'string',
              missingId: option.string_id,
              location: `Option in node '${node.name || node.node_identifier}' references missing string`,
              path: ['data', 'nodes', node.id, 'options', option.id || '', 'string_id'],
            });
          }
        }
      }
    }
  }

  // Legacy structure: sections → fields
  if (data.sections && Array.isArray(data.sections)) {
    for (const section of data.sections) {
      if (!section.fields) continue;
      for (const field of section.fields) {
        if (field.string_id && field.translatable && !validStrings.has(field.string_id)) {
          broken.push({
            entityType: 'object',
            entityId: dialog.id,
            entityName: dialog.name,
            referenceType: 'string',
            missingId: field.string_id,
            location: `Dialog field in section '${section.section_identifier}' references missing string`,
            path: ['data', 'sections', section.id || '', 'fields', field.id, 'string_id'],
          });
        }

        if (field.options) {
          for (const option of field.options) {
            if (option.string_id && option.translatable && !validStrings.has(option.string_id)) {
              broken.push({
                entityType: 'object',
                entityId: dialog.id,
                entityName: dialog.name,
                referenceType: 'string',
                missingId: option.string_id,
                location: `Option in section '${section.section_identifier}' references missing string`,
                path: ['data', 'sections', section.id || '', 'fields', field.id, 'options', option.id || '', 'string_id'],
              });
            }
          }
        }
      }
    }
  }

  return broken;
}
